import { IS_LOCAL_DEV, TURNSTILE_SITE_KEY, TURNSTILE_PROXY_URL } from 'config/envVariable';

/**
 * AI Web Game 安全性組態
 *
 * 本模組用於定義 postMessage 通訊及來源驗證的安全政策，
 * 以防止 XSS 及訊息劫持攻擊。
 *
 * 注意：機器人檢測改用 Cloudflare Turnstile（內嵌於遊戲 HTML 中）。
 */

// ============================================================================
// Turnstile 組態
// ============================================================================

/**
 * Cloudflare Turnstile 設定常數
 *
 * Turnstile Widget 在 Platform 層級建立，避免 Game Blob iframe 的 sandbox 限制。
 * 遊戲完成時將 token 透過 Bridge API 傳遞給平台。
 *
 * @property {string} PROXY_URL - Turnstile Proxy URL
 * @property {string} SITE_KEY - Turnstile Site Key
 * @property {number} TOKEN_EXPIRY_MS - Token 有效期限（毫秒，預設 280 秒）
 * @property {string} UNVERIFIED_TOKEN - 未驗證 token 的標記值
 */
export const TURNSTILE_CONFIG = {
    PROXY_URL: TURNSTILE_PROXY_URL,
    SITE_KEY: TURNSTILE_SITE_KEY,
    TOKEN_EXPIRY_MS: 280000,
    UNVERIFIED_TOKEN: 'UNVERIFIED'
};

// ============================================================================
// Origin 驗證
// ============================================================================

/**
 * 取得目前平台的 origin
 *
 * 遊戲 iframe 透過這個 origin 傳訊息給平台。
 * 回傳當前 window 的 origin（協定+主機名+port）。
 *
 * @returns {string} - 當前 origin（例如 'https://dev.feversocial.com'）
 */
export function getPlatformOrigin() {
    return window.location.origin;
}

/**
 * 檢查來源是否允許進行 postMessage 通訊
 *
 * 重要：因為遊戲採 Blob URL 載入，此驗證需處理兩種情況。
 *
 * Blob URL iframe 的 origin 行為（視瀏覽器而定）：
 * 1. 某些瀏覽器/情況：origin 為 'null'（opaque origin）
 * 2. 某些瀏覽器/情況：origin 繼承父頁面（例如 https://dev.feversocial.com）
 *
 * 安全性說明：
 * - 允許 'null' origin 是安全的，因為：
 *   1. 只有我們注入的 Blob URL iframe 會有 null origin
 *   2. 第三方腳本（GTM, GA, Facebook）的 origin 是它們自己的域名，不會是 null
 *   3. 我們有額外的 payload 驗證層（score, duration 格式檢查）
 *   4. iframe 已用 sandbox 屬性隔離
 *
 * 優點：
 * - 自動支援白標子網域（如 microsoft.feversocial.com, apple.feversocial.com）
 * - 自動支援自訂 CName 網域（如 promo.microsoft.com, event.apple.com）
 * - 不需維護靜態白名單
 * - 適用於 dev/staging/production，無需額外設定
 *
 * @param {string} origin - 需驗證的來源 (event.origin)
 * @returns {boolean} - 若來源合法則回傳 true
 *
 * @example
 * isOriginAllowed('null') // true (Blob URL iframe)
 * isOriginAllowed('https://dev.feversocial.com') // true (same origin)
 * isOriginAllowed('https://malicious.com') // false
 * isOriginAllowed('https://www.google-analytics.com') // false
 */
export function isOriginAllowed(origin) {
    if (!origin || typeof origin !== 'string') {
        return false;
    }

    // 允許 Blob URL iframe 的 null origin（opaque origin）
    // 原因：
    // 1. GameIframe.js 使用 Blob URL 載入遊戲（避免跨域問題並注入 Bridge API）
    // 2. Blob URL iframe 在瀏覽器中會產生 'null' origin（字串，非 null 值）
    // 3. 這是瀏覽器的安全機制，因為 Blob URL 沒有真實的網域或伺服器
    // 安全性說明：
    // - 只有我們注入的 Blob iframe 會有 'null' origin
    // - 第三方腳本（GTM, GA, Facebook）的 origin 是它們自己的域名，不會是 'null'
    // - 我們有額外的 payload 驗證層（gameBridge.js 的 isValidGameResult）
    // - iframe 已用 sandbox 屬性隔離，無法存取敏感資訊
    if (origin === 'null') {
        return true;
    }

    // 也允許同源訊息（向後兼容，某些瀏覽器 Blob URL 會繼承父頁 origin）
    return origin === window.location.origin;
}

/**
 * CDN URL 驗證組態
 *
 * 白名單 CDN 網域（供 phase 2 自訂遊戲上傳使用）。
 * phase 1 在 promoGameTypes.js 內硬編碼 CDN URL。
 *
 * 音效檔案（phase 4.5）也會儲存在這些 CDN 網域中。
 */
export const ALLOWED_CDN_DOMAINS = [
    // AWS S3（企業專屬 bucket）
    'https://assets.fevercdn.com',
    'https://s3.ap-southeast-1.amazonaws.com'
];

/**
 * 驗證 CDN URL
 *
 * 檢查遊戲 CDN URL 是否屬於允許的網域。
 * 供 phase 2 自訂遊戲上傳使用。
 *
 * 開發模式（IS_LOCAL_DEV）下有額外的寬鬆處理，方便本地開發測試。
 *
 * @param {string} url - 需驗證的 CDN URL
 * @returns {boolean} - 若該 URL 符合允許 CDN 則回傳 true
 *
 * @example
 * isAllowedCdnUrl('https://assets.fevercdn.com/game.html') // true
 * isAllowedCdnUrl('https://s3.amazonaws.com/game.html') // false
 */
export function isAllowedCdnUrl(url) {
    if (!url || typeof url !== 'string') {
        return false;
    }

    // 開發模式下的寬鬆處理
    if (IS_LOCAL_DEV) {
        // 允許相對路徑（如 /games/ai-web-game/xxx.html）
        if (url.startsWith('/')) {
            return true;
        }

        try {
            const urlObj = new URL(url);

            // 拒絕危險的 URL scheme（即使在開發環境）
            // eslint-disable-next-line no-script-url
            const dangerousSchemes = ['javascript:', 'data:', 'vbscript:', 'file:'];
            if (dangerousSchemes.some(scheme => urlObj.protocol.startsWith(scheme))) {
                return false;
            }

            // 開發環境允許 localhost、127.0.0.1、local.feversocial.com
            if (
                urlObj.hostname === 'localhost' ||
                urlObj.hostname === '127.0.0.1' ||
                urlObj.hostname === 'local.feversocial.com'
            ) {
                return true;
            }

            // 開發環境也允許白名單 CDN
            const origin = `${urlObj.protocol}//${urlObj.host}`;
            return ALLOWED_CDN_DOMAINS.some(domain => {
                try {
                    const domainObj = new URL(domain);
                    const domainOrigin = `${domainObj.protocol}//${domainObj.host}`;
                    return origin === domainOrigin;
                } catch {
                    return false;
                }
            });
        } catch {
            return false;
        }
    }

    // 正式環境的嚴格驗證
    try {
        const urlObj = new URL(url);

        // localhost 特別處理（開發可允許任意 port）
        if (urlObj.hostname === 'localhost' && urlObj.protocol === 'http:') {
            return true;
        }

        // 正式環境需用 HTTPS
        if (urlObj.protocol !== 'https:') {
            return false;
        }

        // 驗證來源是否為允許的 CDN 網域
        // 採嚴格 origin 比對避免子域攻擊
        const origin = `${urlObj.protocol}//${urlObj.host}`;
        return ALLOWED_CDN_DOMAINS.some(domain => {
            try {
                const domainObj = new URL(domain);
                const domainOrigin = `${domainObj.protocol}//${domainObj.host}`;
                return origin === domainOrigin;
            } catch {
                // 白名單格式錯誤則跳過
                return false;
            }
        });
    } catch (error) {
        console.error('[Security] Invalid CDN URL:', error);
        return false;
    }
}
